import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { BaseUrl } from "../../BaseUrl";
import axios from "axios";
import NotificationService from "../../components/NotificationService/NotificationService";
import "./ResetPassword.css";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [token, setToken] = useState("");

  useEffect(() => {
    const tokenParam = searchParams.get("token");
    if (!tokenParam) {
      setError("This reset link is invalid or missing. Please go back to the login page and request a new password reset.");
      return;
    }
    setToken(tokenParam);
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setError(""); // Clear error when user starts typing
  };

  const validatePassword = (password) => {
    if (password.length < 8) {
      return "Password must be at least 8 characters long";
    }
    if (!/\d/.test(password)) {
      return "Password must contain at least one number";
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return "Password must contain at least one special character";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!token) {
      setError("Invalid reset token");
      return;
    }

    if (!formData.password || !formData.confirmPassword) {
      setError("Please fill in all fields");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${BaseUrl}/api/v1/auth/reset-password`, {
        token,
        password: formData.password,
      });

      if (response.data.success) {
        setSuccess(true);
        NotificationService.notifySuccess("Password reset successfully! You can now log in with your new password.");
        
        // Redirect to login page after 3 seconds
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      } else {
        setError(response.data.message || "Failed to reset password");
      }
    } catch (error) {
      console.error("Reset password error:", error);
      const status = error.response?.status;
      const serverMsg = error.response?.data?.message;

      if (status === 400 && serverMsg?.includes('expired')) {
        setError("This reset link has expired. Please go back to the login page and request a new password reset.");
      } else if (status === 400 && serverMsg) {
        setError(serverMsg);
      } else if (status >= 500) {
        setError("Our servers are having trouble right now. Please try again in a few minutes.");
      } else if (!error.response) {
        setError("Unable to reach the server. Please check your internet connection and try again.");
      } else {
        setError(serverMsg || "Failed to reset password. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="Login_backgorund">
        <div className="reset-password-form">
          <div className="success-message">
            <h2>✅ Password Reset Successful!</h2>
            <p>Your password has been reset successfully.</p>
            <p>You will be redirected to the login page in a few seconds...</p>
            <button 
              onClick={() => navigate("/login")}
              className="go-to-login-btn"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="Login_backgorund">
      <div className="reset-password-form">
        <h2>Reset Your Password</h2>
        <p>Enter your new password below</p>
        
        <form onSubmit={handleSubmit}>
          <input
            type="password"
            name="password"
            placeholder="New Password"
            value={formData.password}
            onChange={handleChange}
            required
          />
          
          <input
            type="password"
            name="confirmPassword"
            placeholder="Confirm New Password"
            value={formData.confirmPassword}
            onChange={handleChange}
            required
          />
          
          {error && (
            <div className="error-message">
              {error}
              {(error.includes('expired') || error.includes('invalid') || error.includes('missing')) && (
                <button
                  type="button"
                  className="error-action-link"
                  onClick={() => navigate("/login")}
                >
                  Go to Login to request a new link
                </button>
              )}
            </div>
          )}

          <button type="submit" disabled={loading || !token}>
            {loading ? "Resetting..." : "Reset Password"}
          </button>
        </form>
        
        <div className="password-requirements">
          <h4>Password Requirements:</h4>
          <ul>
            <li>At least 8 characters long</li>
            <li>One number</li>
            <li>One special character</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;



